import { API_URL } from "./auth-shared";

/**
 * Feature-flag client for the landing page.
 *
 * Fetches the evaluated flag set from the API (`/feature-flags/evaluate`)
 * and exposes pure helpers to read individual flags and variants.
 * Designed to fail open: if the API is unavailable, defaults take over
 * and the page still renders.
 */

export type FlagPlatform = "LANDING" | "ANDROID" | "IOS";

interface EvaluatedFlag {
  key: string;
  enabled: boolean;
  variant: string | null;
}

/**
 * Result of {@link fetchFlags}: map of flag key → resolved evaluation.
 *
 * Both `enabled` and `variant` are kept so callers can pick the right
 * abstraction: {@link isFlagOn} for boolean toggles, {@link getVariant}
 * for multivariate / A-B tests.
 */
export type FlagsMap = Record<
  string,
  { enabled: boolean; variant: string | null }
>;

/**
 * Fetches all flags evaluated for the given platform (and optionally
 * scoped to a `userId` for sticky variant assignment).
 *
 * Fails open: returns `{}` on error, leaving callers to apply defaults
 * via {@link isFlagOn} / {@link getVariant}. For the public landing the
 * default is "everything on" — better to over-render than to hide
 * sections because of a transient network blip.
 *
 * Caching: NONE (`cache: "no-store"`). The previous 1-hour revalidate
 * window was paired with a `revalidatePath('/', 'layout')` call from
 * the panel BFF — that doesn't work cross-app: panel and landing are
 * separate Next.js processes in different containers, so the panel's
 * cache invalidation never reaches the landing's cache and dashboard
 * toggles sat invisible for up to 1 hour. Same fix as
 * `remote-config.ts`; if traffic ever grows enough that the extra
 * call hurts, wire a cross-app revalidation webhook there.
 */
export async function fetchFlags(
  platform: FlagPlatform,
  userId?: string,
): Promise<FlagsMap> {
  try {
    const params = new URLSearchParams({ platform });
    if (userId) params.set("userId", userId);
    const res = await fetch(
      `${API_URL}/feature-flags/evaluate?${params.toString()}`,
      { cache: "no-store" },
    );
    if (!res.ok) return {};
    const data = (await res.json()) as EvaluatedFlag[];
    const map: FlagsMap = {};
    for (const f of data) {
      map[f.key] = { enabled: f.enabled, variant: f.variant };
    }
    return map;
  } catch {
    return {};
  }
}

/**
 * Reads a single boolean flag with a default fallback.
 *
 * Returns `defaultOn` when:
 * - The flag isn't in the map (API failed, or new flag not seeded).
 * - This lets new code roll out before the flag is configured upstream.
 *
 * @param defaultOn — Value when the flag is missing. Default `true`.
 */
export function isFlagOn(
  flags: FlagsMap,
  key: string,
  defaultOn = true,
): boolean {
  if (key in flags) return flags[key].enabled;
  return defaultOn;
}

/**
 * Reads the assigned variant of a multivariate flag.
 *
 * Returns `defaultVariant` when the flag is missing OR the flag is a
 * pure boolean toggle (variant === null). Use this for A/B testing in
 * the landing or mobile apps.
 *
 * @param defaultVariant — Variant name to assume when missing. Default `"control"`.
 */
export function getVariant(
  flags: FlagsMap,
  key: string,
  defaultVariant = "control",
): string {
  if (key in flags && flags[key].variant !== null) {
    return flags[key].variant as string;
  }
  return defaultVariant;
}
